import { useMemo, useState } from "react";
import {
  FEATURE_STATUS_META,
  FeatureRequestStatus,
  ProductFeature,
  ProductFeatureStatus,
  getFeatureStatusMeta,
} from "../../../api/featureRequests";
import { useProductFeaturesQuery } from "../hooks/useFeatureRequestsQueries";
import { formatDate } from "../utils";
import DeployFeatureDialog from "./DeployFeatureDialog";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import { Column } from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import DSBox from "../../system-design/DSBox";
import DSAlert from "../../system-design/DSAlert";
import DSChip from "../../system-design/DSChip";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSEnumSelect from "../../system-design/DSEnumSelect";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSTextField from "../../system-design/DSTextField";
import DSTypography from "../../system-design/DSTypography";

const ALL = "all";
const NOT_DEPLOYED = "not_deployed";
type Filter = ProductFeatureStatus | typeof ALL | typeof NOT_DEPLOYED;

const FILTER_OPTIONS: { value: Filter; label: string }[] = [
  { value: ALL, label: "All features" },
  { value: NOT_DEPLOYED, label: "Not deployed yet" },
  ...Object.values(ProductFeatureStatus).map((status) => ({
    value: status,
    label: FEATURE_STATUS_META[status].label,
  })),
];

const COLUMNS: Column<ProductFeature>[] = [
  { field: "title", label: "Feature", sortable: true, size: 340 },
  { field: "status", label: "Status", sortable: true },
  { field: "customerCount", label: "Customers", sortable: true },
  { field: "openCount", label: "Requests", sortable: true },
  { field: "deployedVersion", label: "Deployed", sortable: true },
  { field: "lastRequestedAt", label: "Last requested", sortable: true },
];

type SortState = Record<string, 1 | -1>;

// Clicking a count chip jumps to the Requests tab filtered to it.
const CountChip = (props: {
  count: number;
  label: string;
  color: "default" | "warning" | "success";
  onClick: () => void;
}) =>
  props.count > 0 ? (
    <DSChip
      size="small"
      variant="outlined"
      color={props.color}
      label={`${props.count} ${props.label}`}
      onClick={(e) => {
        e.stopPropagation();
        props.onClick();
      }}
    />
  ) : null;

const FeaturesTab = (props: {
  onOpenFeature: (featureId: string) => void;
  onShowRequests: (featureId: string, status: FeatureRequestStatus) => void;
}) => {
  const { data: features, isLoading, isError } = useProductFeaturesQuery();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>(ALL);
  // Most-requested first: the point of the page is seeing demand.
  const [sortState, setSortState] = useState<SortState>({
    customerCount: -1,
  });
  const [deploying, setDeploying] = useState<ProductFeature | null>(null);

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = (features ?? []).filter((feature) => {
      if (filter === NOT_DEPLOYED && feature.deployedVersion) return false;
      if (
        filter !== ALL &&
        filter !== NOT_DEPLOYED &&
        feature.status !== filter
      )
        return false;
      return (
        !query ||
        feature.title.toLowerCase().includes(query) ||
        (feature.description ?? "").toLowerCase().includes(query)
      );
    });
    const [field, direction] = Object.entries(sortState)[0] ?? [];
    if (!field) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[field as keyof ProductFeature] ?? "";
      const bv = b[field as keyof ProductFeature] ?? "";
      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true });
      return cmp * direction;
    });
  }, [features, search, filter, sortState]);

  const handleSort = (field: keyof ProductFeature) =>
    setSortState((prev) => {
      const current = prev[field as string];
      if (!current) return { [field]: -1 };
      if (current === -1) return { [field]: 1 };
      return {};
    });

  const rowBuilder = (feature: ProductFeature) => {
    const meta = getFeatureStatusMeta(feature.status);
    const show = (status: FeatureRequestStatus) => () =>
      props.onShowRequests(feature.id, status);
    return (
      <>
        <DSTableCell>
          <DSTypography sx={{ fontWeight: 600 }}>{feature.title}</DSTypography>
          {feature.description && (
            <DSTypography
              sx={{
                fontSize: 12,
                color: "text.secondary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {feature.description}
            </DSTypography>
          )}
        </DSTableCell>
        <DSTableCell>
          <DSChip
            label={meta.label}
            color={meta.color}
            size="small"
            variant="outlined"
          />
        </DSTableCell>
        <DSTableCell>
          <DSTypography sx={{ fontWeight: 700 }}>
            {feature.customerCount}
          </DSTypography>
        </DSTableCell>
        <DSTableCell>
          <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            <CountChip
              count={feature.openCount}
              label="open"
              color="default"
              onClick={show(FeatureRequestStatus.Open)}
            />
            <CountChip
              count={feature.potentiallyResolvedCount}
              label="to review"
              color="warning"
              onClick={show(FeatureRequestStatus.PotentiallyResolved)}
            />
            <CountChip
              count={feature.fulfilledCount}
              label="fulfilled"
              color="success"
              onClick={show(FeatureRequestStatus.Fulfilled)}
            />
            {feature.requestCount === 0 && "—"}
          </DSBox>
        </DSTableCell>
        <DSTableCell>
          {feature.deployedVersion ? (
            <>
              <DSTypography sx={{ fontWeight: 600 }}>
                {feature.deployedVersion}
              </DSTypography>
              <DSTypography sx={{ fontSize: 12, color: "text.secondary" }}>
                {formatDate(feature.deployedAt)}
              </DSTypography>
            </>
          ) : (
            <DSSecondaryButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDeploying(feature);
              }}
            >
              Mark deployed
            </DSSecondaryButton>
          )}
        </DSTableCell>
        <DSTableCell>{formatDate(feature.lastRequestedAt)}</DSTableCell>
      </>
    );
  };

  if (isLoading) {
    return (
      <DSBox sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <DSCircularProgress />
      </DSBox>
    );
  }
  if (isError) {
    return (
      <DSAlert severity="error">
        Failed to load features. Please try again.
      </DSAlert>
    );
  }

  return (
    <>
      <DSBox sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <DSTextField
          size="small"
          placeholder="Search features..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
        />
        <DSEnumSelect<Filter>
          size="small"
          value={filter}
          options={FILTER_OPTIONS}
          onChange={setFilter}
          sx={{ minWidth: 220 }}
        />
      </DSBox>

      {rows.length === 0 ? (
        <DSTypography sx={{ textAlign: "center", py: 6 }}>
          {features?.length
            ? "No features match the current filters."
            : "No features yet. Click “New feature” or “Add request” to create one."}
        </DSTypography>
      ) : (
        <DSBaseTable<ProductFeature>
          data={rows}
          columns={COLUMNS}
          rowBuilder={rowBuilder}
          onPressRow={(feature) => props.onOpenFeature(feature.id)}
          onSort={handleSort}
          sortState={sortState}
          defaultRowsPerPage={20}
        />
      )}

      {deploying && (
        <DeployFeatureDialog
          open
          feature={deploying}
          onClose={() => setDeploying(null)}
        />
      )}
    </>
  );
};

export default FeaturesTab;
